import { useEffect, useRef, useState } from 'react';
import { createLoginLogoShaderRuntime } from './loginLogoShaderRuntime';
import { readShaderPalette } from './loginShaderPalette';

const LOGO_SOURCE = '/brand/modul-mark.svg';

function getMotionQuery() {
  if (typeof window.matchMedia !== 'function') {
    return {
      matches: false,
      addEventListener: () => {},
      removeEventListener: () => {},
    };
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)');
}

function loadLogoImage() {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = LOGO_SOURCE;
  });
}

function LoginLogoShader({ className = '' }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return undefined;
    }

    let disposed = false;
    let animationFrame = null;
    let runtime = null;
    let reducedMotion = false;
    const motionQuery = getMotionQuery();

    const stop = () => {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
    };

    const drawFrame = (timestamp = 0) => {
      runtime?.drawFrame(timestamp, { reducedMotion });
    };

    const draw = (timestamp) => {
      animationFrame = null;
      drawFrame(timestamp);

      if (!reducedMotion && !document.hidden) {
        animationFrame = requestAnimationFrame(draw);
      }
    };

    const start = () => {
      if (animationFrame === null && !reducedMotion && !document.hidden) {
        animationFrame = requestAnimationFrame(draw);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }

      drawFrame(performance.now());
      start();
    };

    const handleMotionChange = (event) => {
      reducedMotion = event.matches;

      if (reducedMotion) {
        stop();
        drawFrame(0);
        return;
      }

      drawFrame(performance.now());
      start();
    };

    const handleContextLost = (event) => {
      event.preventDefault();
      stop();
      setReady(false);
    };

    const handlePointerMove = (event) => {
      const timestamp = runtime?.handlePointerMove(event) || performance.now();

      if (reducedMotion) {
        drawFrame(timestamp);
      }
    };

    const handlePointerLeave = () => {
      runtime?.handlePointerLeave();

      if (reducedMotion) {
        drawFrame(performance.now());
      }
    };

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          drawFrame(performance.now());
        })
      : null;

    loadLogoImage()
      .then((image) => {
        if (disposed) {
          return;
        }

        let gl = null;

        try {
          gl = canvas.getContext('webgl', {
            alpha: true,
            antialias: false,
            depth: false,
            premultipliedAlpha: false,
            preserveDrawingBuffer: false,
            stencil: false,
            powerPreference: 'low-power',
          });
        } catch {
          gl = null;
        }

        if (!gl) {
          return;
        }

        try {
          runtime = createLoginLogoShaderRuntime({
            canvas,
            gl,
            image,
            palette: readShaderPalette(),
          });
        } catch {
          return;
        }

        reducedMotion = motionQuery.matches;
        resizeObserver?.observe(canvas);
        canvas.addEventListener('webglcontextlost', handleContextLost);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pointermove', handlePointerMove, { passive: true });
        window.addEventListener('pointerleave', handlePointerLeave);
        motionQuery.addEventListener('change', handleMotionChange);

        drawFrame(0);
        setReady(true);
        start();
      })
      .catch(() => {});

    return () => {
      disposed = true;
      stop();
      resizeObserver?.disconnect();
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', handlePointerLeave);
      motionQuery.removeEventListener('change', handleMotionChange);
      runtime?.dispose();
    };
  }, []);

  const classes = [
    'login-page-brand-mark__shader',
    ready ? 'is-ready' : null,
    className,
  ].filter(Boolean).join(' ');

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={classes}
    />
  );
}

export default LoginLogoShader;
